if (!customElements.get('shp-product-information')) {
  customElements.define('shp-product-information', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized || !this.isConnected) return;
      if (!this.querySelector('[data-shp-care-content]')) {
        requestAnimationFrame(() => this.connectedCallback());
        return;
      }
      this.initialized = true;
      const description = this.querySelector('[data-shp-description]');
      if (description) this.organizeDescription(description);
      this.renderPolicy('delivery', /^delivery(?:\s+policy)?$/i);
      this.renderPolicy('returns', /^(?:return|refund)s?(?:\s+policy)?$/i);
    }

    normalize(value) {
      return value.replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
    }

    hasMeasurement(element) {
      const text = element.tagName === 'TABLE'
        ? [...element.querySelectorAll('th, td')].map(cell => this.normalize(cell.textContent)).join(' ')
        : this.normalize(element.textContent);
      if (!text || text.length > 240 && element.tagName !== 'TABLE') return false;
      if (element.tagName === 'TABLE' && /\b(?:width|depth|height|length|dimensions)\b/i.test(text) && /\d/.test(text)) return true;
      return /\d(?:[\d.,]*)\s*(?:in(?:ches)?\.?|cm|mm|ft|feet|["″”])\s*[LWDH]\b/i.test(text) ||
        /\b(?:width|depth|height|length)\s*[:=]?\s*\d[\d.,]*\s*(?:in\b|inches\b|cm\b|mm\b|ft\b|["″”])/i.test(text) ||
        /\d[\d.,]*\s*(?:["″”]|in\b|cm\b|mm\b)?\s*[WLDH]\s*[x×]\s*\d/i.test(text) ||
        /\d[\d.,]*\s*[x×]\s*\d[\d.,]*\s*[x×]\s*\d[\d.,]*\s*(?:in\b|inches\b|cm\b|mm\b|ft\b|["″”])/i.test(text) ||
        /^(?:weight\s*[:=]?\s*)?\d[\d.,]*\s*(?:lb(?:s)?\.?|kg)\b/i.test(text);
    }

    isHeading(element) {
      if (/^H[1-6]$/.test(element.tagName)) return true;
      if (!['P', 'DIV'].includes(element.tagName)) return false;
      const children = [...element.children];
      return children.length > 0 && children.every(el => ['B', 'STRONG'].includes(el.tagName)) &&
        this.normalize(element.textContent) === this.normalize(children.map(el => el.textContent).join(' '));
    }

    getBlocks(source) {
      const blocks = [];
      let line = document.createElement('p');
      const flush = () => {
        if (this.normalize(line.textContent) || line.querySelector('img, iframe, video')) blocks.push(line);
        line = document.createElement('p');
      };
      const walk = parent => {
        for (const node of [...parent.childNodes]) {
          if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'BR') {
            flush();
          } else if (node.nodeType === Node.ELEMENT_NODE && /^(DIV|SECTION|ARTICLE|P)$/.test(node.tagName) && node.querySelector('br, p, h1, h2, h3, h4, table, ul, ol')) {
            flush();
            walk(node);
          } else if (node.nodeType === Node.ELEMENT_NODE && /^(P|H[1-6]|TABLE|UL|OL|BLOCKQUOTE|DIV|FIGURE|IFRAME|VIDEO)$/.test(node.tagName)) {
            flush();
            blocks.push(node);
          } else {
            line.appendChild(node);
          }
        }
        flush();
      };
      walk(source);
      return blocks;
    }

    organizeDescription(source) {
      const dimensions = this.querySelector('[data-shp-dimensions-content]');
      const care = this.querySelector('[data-shp-care-content]');
      const blocks = this.getBlocks(source);
      const description = document.createDocumentFragment();
      let mode = 'description';
      for (let index = 0; index < blocks.length; index++) {
        const block = blocks[index];
        const text = this.normalize(block.textContent).replace(/\s*\((?:inches|in|cm|mm)\)\s*$/i, '').replace(/:$/, '');
        const heading = this.isHeading(block);
        if (heading && /^(?:(?:weights?\s*(?:&|and)\s*)?dimensions?(?:\s*(?:&|and)\s*weights?)?|measurements?|overall\s+dimensions?)$/i.test(text)) {
          mode = 'dimensions';
        } else if (heading && /^(?:care(?:\s*(?:&|and)\s*cleaning)?(?:\s+instructions)?|fabric\s+details|materials?(?:\s*(?:&|and)\s*care)?)$/i.test(text)) {
          mode = 'care';
        } else if (heading && !this.hasMeasurement(blocks[index + 1] || document.createElement('span'))) {
          mode = 'description';
        }
        if (heading && blocks[index + 1] && this.hasMeasurement(blocks[index + 1])) {
          const entry = document.createElement('div');
          entry.className = 'shp-dimension-entry';
          entry.append(block, blocks[++index]);
          dimensions.appendChild(entry);
        } else if (this.hasMeasurement(block) || mode === 'dimensions') {
          dimensions.appendChild(block);
        } else if (mode === 'care') {
          care.appendChild(block);
        } else {
          description.appendChild(block);
        }
      }
      source.replaceChildren(description);
      if (!this.normalize(source.textContent) && !source.querySelector('img, video, iframe, table')) source.closest('details').hidden = true;
      const hasDimensions = this.normalize(dimensions.textContent) && [...dimensions.querySelectorAll('p, table, li, td')].some(node => this.hasMeasurement(node));
      if (hasDimensions) {
        dimensions.closest('details').hidden = false;
      } else if (dimensions.childNodes.length) {
        source.append(...dimensions.childNodes);
        source.closest('details').hidden = false;
      }
      if ([...care.children].some(node => !this.isHeading(node) && this.normalize(node.textContent))) {
        care.closest('details').hidden = false;
      } else if (care.childNodes.length) {
        source.append(...care.childNodes);
        source.closest('details').hidden = false;
      }
    }

    renderPolicy(kind, headingPattern) {
      const template = this.querySelector(`[data-shp-${kind}-source]`);
      const target = this.querySelector(`[data-shp-${kind}-content]`);
      if (!template || !target) return;
      const heading = [...template.content.querySelectorAll('h1, h2, h3, h4, h5, h6')].find(el => headingPattern.test(this.normalize(el.textContent)));
      if (!heading) return;
      const level = Number(heading.tagName.slice(1));
      for (let node = heading.nextSibling; node; node = node.nextSibling) {
        if (node.nodeType === Node.ELEMENT_NODE && /^H[1-6]$/.test(node.tagName) && Number(node.tagName.slice(1)) <= level) break;
        if (node.nodeType === Node.ELEMENT_NODE && /^(SCRIPT|STYLE|LINK|META)$/.test(node.tagName)) continue;
        const clone = node.cloneNode(true);
        if (clone.querySelectorAll) clone.querySelectorAll('script, style, link, meta').forEach(el => el.remove());
        target.appendChild(clone);
      }
    }
  });
}
